import React from 'react';
import {
  IAlertThresholdCompareOp,
  IAlertThresholdCustom,
  IComparisonConfig,
} from '../../../utils/apollo/graphqlSchema';
import { NumberInputField } from '../../Form';
import { SingleSelectMenu } from '../../Dropdown';
import styled from 'styled-components';
import KubitTooltip from '../../KubitTooltip';
import { SvgIcon } from '../../Icon';
import { comparisonConfigDisplay } from '../../../utils/comparisonConfig';
import { theme } from '../../../styles/theme';

interface IProps {
  comparisonConfig: IComparisonConfig | null;
  compareOps: IAlertThresholdCompareOp[];
  threshold: IAlertThresholdCustom;
  setThreshold: (threshold: IAlertThresholdCustom) => void;
  allowAddMore: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
}

const HorizontalWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const FIXED_COMPARE_OP_WIDTH = 180;

const NoMarginNumberInputField = styled(NumberInputField)`
  margin-right: 0;
  margin-left: 0;
  width: 160px;
  > input {
    width: 100%;
  }
`;

const ComparisonConfigDisplayWtapper = styled.div`
  font-size: 10px;
  margin-left: ${FIXED_COMPARE_OP_WIDTH + 4}px;
`;

const ButtonSvgIcon = styled(SvgIcon)`
  margin-left: 4px;
  width: 12px;
  height: 12px;
  cursor: pointer;
`;

const PERCENT_OPT_VALUE = '%';
const ABSOLUTE_OPT_VALUE = '#';
const VALUE_IS_PERCENT_OR_ABSOLUTE_OPTIONS = [
  {
    text: '#',
    value: ABSOLUTE_OPT_VALUE,
    tooltip: { content: 'absolute value' },
  },
  { text: '%', value: PERCENT_OPT_VALUE, tooltip: { content: 'percent' } },
];

export default ({
  comparisonConfig,
  compareOps,
  threshold,
  setThreshold,
  allowAddMore,
  onAdd,
  onRemove,
}: IProps) => {
  const setCompareValue = (newValue: number | null) => {
    if (newValue === null) {
      return;
    }
    setThreshold({ ...threshold, compareValue: newValue });
  };

  const isComparisonAnalysis = !!comparisonConfig;
  const setCompareOp = (newOpKey: string) => {
    const newOp = compareOps.find((co) => co.key === newOpKey);
    const validComparison = !newOp?.requireComparison || isComparisonAnalysis;
    if (newOp && validComparison) {
      setThreshold({ ...threshold, compareOp: newOp });
    }
  };

  const setValueIsPercentOrAbsolute = (newOptValue: string) => {
    const valueIsPercent = newOptValue === PERCENT_OPT_VALUE;
    setThreshold({ ...threshold, valueIsPercent });
  };

  const compareOpsDropdown = (
    <SingleSelectMenu
      options={compareOps.map((o) => {
        const isDisabled = o.requireComparison && !isComparisonAnalysis;
        return {
          text: o.display,
          value: o.key,
          disabled: isDisabled ?? undefined,
          tooltip: isDisabled
            ? { content: 'Only available with Comparison Analyses' }
            : undefined,
        };
      })}
      value={threshold.compareOp.key}
      onChange={setCompareOp}
      customWidth={`${FIXED_COMPARE_OP_WIDTH}px`}
    />
  );

  return (
    <>
      <HorizontalWrapper>
        {compareOpsDropdown}
        <NoMarginNumberInputField
          value={threshold.compareValue}
          onChange={setCompareValue}
          precision={2}
        />
        {isComparisonAnalysis && threshold.compareOp?.requireComparison && (
          <SingleSelectMenu
            options={VALUE_IS_PERCENT_OR_ABSOLUTE_OPTIONS}
            value={
              threshold.valueIsPercent ? PERCENT_OPT_VALUE : ABSOLUTE_OPT_VALUE
            }
            onChange={setValueIsPercentOrAbsolute}
          />
        )}
        {allowAddMore && onAdd && (
          <KubitTooltip noOpenDelay={true} content="Add condition">
            <ButtonSvgIcon
              customFill={theme.colors.mainBlue}
              name={'filter-add'}
              onClick={onAdd}
            />
          </KubitTooltip>
        )}
        {onRemove && (
          <KubitTooltip noOpenDelay={true} content="Remove condition">
            <ButtonSvgIcon
              customFill={theme.colors.mainBlue}
              name="icon-x"
              onClick={onRemove}
            />
          </KubitTooltip>
        )}
      </HorizontalWrapper>
      {comparisonConfig && threshold.compareOp?.requireComparison && (
        <ComparisonConfigDisplayWtapper>
          Compared to {comparisonConfigDisplay(comparisonConfig)}
        </ComparisonConfigDisplayWtapper>
      )}
    </>
  );
};
